import { useMemo, useState, type UIEvent } from 'react'
import { useTranslation } from 'react-i18next'
import type { StatementRow } from '../../api/types'
import { cx } from '../../lib/cx'
import { AmountText } from '../AmountDisplay'
import { TextField } from '../TextField'
import { MEMBER_COLUMNS, type StatementColumn } from './columns'

export interface StatementTableProps {
  rows: StatementRow[]
  /** Which figures to show, in order. Defaults to the member view. */
  columns?: readonly StatementColumn[]
  totals?: Partial<Record<StatementColumn, number>>
  /** Her own row, highlighted and announced. */
  highlightMemberNo?: number
  /** "Nov 2026": the caption says which month the table is for. */
  period: string
  /** A caption override, e.g. "Group statement, Nov 2026". */
  caption?: string
  className?: string
}

/** Owed figures read "-K… owed", never colour alone. */
const OWED: ReadonlySet<StatementColumn> = new Set(['loan_principal', 'loan_interest', 'receivables'])

/** Above this many rows the table draws only what is on screen (groups of 100 or more). */
const VIRTUAL_FROM = 100
const ROW_HEIGHT = 48
const WINDOW_ROWS = 18

/**
 * Group figures by member number, never names (PRD 16). The member-number column stays
 * put while the figures scroll sideways; her own row is highlighted. Large groups get a
 * member-number search and only the visible rows are drawn.
 */
export function StatementTable({ rows, columns = MEMBER_COLUMNS, totals, highlightMemberNo, period, caption,
  className }: StatementTableProps) {
  const { t } = useTranslation()
  const [search, setSearch] = useState('')
  const [scrollTop, setScrollTop] = useState(0)

  const shown = useMemo(() => {
    const wanted = search.trim()
    return wanted ? rows.filter((row) => String(row.member_no).startsWith(wanted)) : rows
  }, [rows, search])

  const virtual = shown.length >= VIRTUAL_FROM
  const first = virtual ? Math.max(0, Math.floor(scrollTop / ROW_HEIGHT) - 3) : 0
  const last = virtual ? Math.min(shown.length, first + WINDOW_ROWS + 6) : shown.length
  const onScroll = (event: UIEvent<HTMLDivElement>) => {
    if (virtual) setScrollTop(event.currentTarget.scrollTop)
  }

  return (
    <div className={cx('statement-table', className)}>
      {rows.length > 12 && (
        <TextField label={t('money.statement.find')} hint={t('money.statement.findHint')} value={search}
          onChange={(event) => setSearch(event.target.value.replace(/\D/g, ''))} inputMode="numeric"
          fieldClassName="statement-table__search" />
      )}
      <div className={cx('statement-table__scroll', 'glass', virtual && 'statement-table__scroll--virtual')}
        onScroll={onScroll} tabIndex={0} role="region"
        aria-label={caption ?? t('money.statement.caption', { period })}>
        <table>
          <caption className="visually-hidden">{caption ?? t('money.statement.caption', { period })}</caption>
          <thead>
            <tr>
              <th scope="col">{t('money.statement.member')}</th>
              {columns.map((column) => (
                <th scope="col" key={column}>{t(`money.statement.columns.${column}`)}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {virtual && first > 0 && <tr aria-hidden="true" style={{ height: first * ROW_HEIGHT }} />}
            {shown.slice(first, last).map((row) => {
              const mine = row.member_no === highlightMemberNo
              return (
                <tr key={row.member_no} className={cx(mine && 'statement-table__mine')}
                  aria-current={mine ? 'true' : undefined}>
                  <th scope="row">
                    {t('money.statement.memberNo', { no: row.member_no })}
                    {mine && <span className="statement-table__you"> {t('money.statement.you')}</span>}
                  </th>
                  {columns.map((column) => (
                    <td key={column}>
                      <AmountText valueNgwee={row[column] ?? 0}
                        tone={OWED.has(column) && (row[column] ?? 0) > 0 ? 'owed' : 'neutral'} />
                    </td>
                  ))}
                </tr>
              )
            })}
            {virtual && last < shown.length && (
              <tr aria-hidden="true" style={{ height: (shown.length - last) * ROW_HEIGHT }} />
            )}
            {shown.length === 0 && (
              <tr>
                <td colSpan={columns.length + 1} className="statement-table__empty">
                  {t('money.statement.noMatch', { no: search })}
                </td>
              </tr>
            )}
          </tbody>
          {totals && !search && (
            <tfoot>
              <tr>
                <th scope="row">{t('money.statement.total')}</th>
                {columns.map((column) => (
                  <td key={column}>
                    <AmountText valueNgwee={totals[column] ?? 0}
                      tone={OWED.has(column) && (totals[column] ?? 0) > 0 ? 'owed' : 'neutral'} />
                  </td>
                ))}
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  )
}
